import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Grid,
  Avatar,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  Card,        
  CardContent, 
} from '@mui/material';
import {
  Search,
  Refresh,
  History,
  Add,
  Remove,
  Edit,
  Delete,
  Person,
  Close,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';

// Fix the API base URL - remove any trailing slashes
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost/Hotel/api').replace(/\/$/, '');

const getHotelCode = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
  } catch (error) {
    console.error('Error getting hotel code:', error);
    return '';
  }
};

const StatCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const actionColors = {
  create: { color: 'success', icon: <Add />, label: 'Created' },
  update: { color: 'info', icon: <Edit />, label: 'Updated' },
  delete: { color: 'error', icon: <Delete />, label: 'Deleted' },
  stock_add: { color: 'success', icon: <Add />, label: 'Stock Added' },
  stock_remove: { color: 'warning', icon: <Remove />, label: 'Stock Removed' },
};

const InventoryLogs = ({ open, onClose, itemId = null }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  const [actionType, setActionType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedItem, setSelectedItem] = useState(itemId || '');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const actionTypes = [
    { value: 'create', label: 'Created' },
    { value: 'update', label: 'Updated' },
    { value: 'delete', label: 'Deleted' },
    { value: 'stock_add', label: 'Stock Added' },
    { value: 'stock_remove', label: 'Stock Removed' },
  ];

  const loadLogs = async () => {
    const hotelCode = getHotelCode();
    
    if (!hotelCode) {
      console.error('No hotel code found');
      setError('No hotel code found. Please log in again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const params = {
        hotel_code: hotelCode,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      
      if (selectedItem) params.item_id = selectedItem;
      if (actionType) params.action_type = actionType;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      // Build URL without double slashes
      const apiUrl = `${API_BASE_URL}/hotel/F&B/inventory_logs.php`;
      
      console.log('Fetching logs from:', apiUrl);
      console.log('With params:', params);
      
      const response = await axios.get(apiUrl, { params });
      
      console.log('Response:', response.data);
      
      if (response.data.success) {
        setLogs(response.data.logs || []);
        setTotal(response.data.total || 0);
        setSummary(response.data.summary || {
          total_logs: 0,
          stock_adds: 0,
          stock_removes: 0,
          creations: 0,
          updates: 0,
          deletions: 0,
          total_quantity_changed: 0
        });
      } else {
        setError(response.data.error || 'Failed to load logs');
        setLogs([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      setError(error.response?.data?.error || error.message || 'Failed to load logs');
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadLogs();
    }
  }, [open, page, rowsPerPage, actionType, startDate, endDate, selectedItem]);

  const handleRefresh = () => {
    loadLogs();
  };

  const handleResetFilters = () => {
    setActionType('');
    setStartDate('');
    setEndDate('');
    setSelectedItem('');
    setPage(0);
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getActionConfig = (actionType) => {
    return actionColors[actionType] || { color: 'default', icon: <History />, label: actionType };
  };

  const renderSummaryCards = () => {
    if (!summary) return null;
    
    const cards = [
      { title: 'Total Actions', value: summary.total_logs || 0, icon: <History />, color: 'primary' },
      { title: 'Stock Additions', value: summary.stock_adds || 0, icon: <Add />, color: 'success' },
      { title: 'Stock Removals', value: summary.stock_removes || 0, icon: <Remove />, color: 'warning' },
      { title: 'Items Created', value: summary.creations || 0, icon: <Add />, color: 'info' },
      { title: 'Items Updated', value: summary.updates || 0, icon: <Edit />, color: 'info' },
      { title: 'Items Deleted', value: summary.deletions || 0, icon: <Delete />, color: 'error' },
    ];
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={2} key={index}>
            <StatCard>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Avatar sx={{ bgcolor: `${card.color}.light`, margin: '0 auto 8px auto', width: 40, height: 40 }}>
                  {card.icon}
                </Avatar>
                <Typography variant="h5" fontWeight="bold" color={`${card.color}.main`}>
                  {card.value}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {card.title}
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History fontSize="large" color="primary" />
            <Typography variant="h5" fontWeight="bold">
              Inventory Activity Log
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Summary Cards */}
        {summary && renderSummaryCards()}
        
        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* Filters */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                  label="Action Type"
                >
                  <MenuItem value="">All Actions</MenuItem>
                  {actionTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleResetFilters}
                  fullWidth
                >
                  Reset Filters
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleRefresh}
                  startIcon={<Refresh />}
                >
                  Refresh
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Logs Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell><strong>Date & Time</strong></TableCell>
                <TableCell><strong>Item</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
                <TableCell><strong>Quantity Change</strong></TableCell>
                <TableCell><strong>Performed By</strong></TableCell>
                <TableCell align="center"><strong>Details</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No activity logs found. Start adding or updating inventory items to see logs.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => {
                  const actionConfig = getActionConfig(log.action_type);
                  const isStockChange = log.action_type === 'stock_add' || log.action_type === 'stock_remove';
                  const quantityChange = parseFloat(log.quantity_change);
                  
                  return (
                    <TableRow key={log.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {log.item_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={log.category} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={actionConfig.icon}
                          label={actionConfig.label}
                          color={actionConfig.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {isStockChange && quantityChange !== 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {quantityChange > 0 ? (
                              <Chip 
                                icon={<Add />}
                                label={`+${quantityChange}`}
                                color="success"
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Chip 
                                icon={<Remove />}
                                label={`${quantityChange}`}
                                color="error"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        )}
                        {!isStockChange && log.action_type === 'create' && (
                          <Chip 
                            label={`Initial: ${log.new_quantity || 0}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person fontSize="small" color="action" />
                          <Typography variant="body2">
                            {log.performed_by_name || log.performed_by || 'System'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewDetails(log)}>
                            <History fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </DialogContent>
      
      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <History color="primary" />
              <Typography variant="h6">Activity Details</Typography>
            </Box>
            <IconButton onClick={() => setDetailsOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Date & Time</Typography>
                  <Typography variant="body1">{formatDate(selectedLog.created_at)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Item</Typography>
                  <Typography variant="body1">{selectedLog.item_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Category</Typography>
                  <Typography variant="body1">{selectedLog.category}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Action Type</Typography>
                  <Chip 
                    icon={getActionConfig(selectedLog.action_type).icon}
                    label={getActionConfig(selectedLog.action_type).label}
                    color={getActionConfig(selectedLog.action_type).color}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Performed By</Typography>
                  <Typography variant="body1">{selectedLog.performed_by_name || selectedLog.performed_by || 'System'}</Typography>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Changes
              </Typography>
              
              <Grid container spacing={2}>
                {selectedLog.previous_quantity !== null && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Previous Quantity</Typography>
                      <Typography variant="body1">{selectedLog.previous_quantity}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">New Quantity</Typography>
                      <Typography variant="body1">{selectedLog.new_quantity}</Typography>
                    </Grid>
                  </>
                )}
                
                {selectedLog.previous_price_dine_in !== null && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Previous Dine In Price</Typography>
                      <Typography variant="body1">₦{parseFloat(selectedLog.previous_price_dine_in).toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">New Dine In Price</Typography>
                      <Typography variant="body1">₦{parseFloat(selectedLog.new_price_dine_in).toFixed(2)}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
              
              {selectedLog.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                  <Typography variant="body2">{selectedLog.notes}</Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default InventoryLogs;